import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useGuide } from "@/lib/api/hooks";
import { track } from "@/lib/analytics";
import { StepModule } from "./components/StepModule";
import { withGuideMedia } from "./selfInjectionGuide";

// Matches `--duration-base` in tokens.css — keep in sync so the close
// transition finishes before we navigate away.
const DRAWER_MS = 200;

export function GuideDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useGuide(slug);
  const guide = useMemo(
    () => (data ? withGuideMedia(data) : undefined),
    [data],
  );
  // Start closed so Radix renders one frame in `data-state="closed"` then
  // transitions to `open` — that gives us the slide-up entrance. Without
  // this the dialog mounts already-open and snaps into place.
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const closing = useRef(false);
  useEffect(() => {
    closing.current = false;
    return () => clearTimeout(closeTimer.current);
  }, [slug]);
  useEffect(() => {
    if (guide && !closing.current) setOpen(true);
  }, [guide]);

  if (isLoading)
    return (
      <div role="status" className="sr-only">
        Opening guide…
      </div>
    );

  if (error || !guide) {
    return (
      <div className="px-15 md:px-40 max-w-[65rem] mx-auto w-full">
        <Link
          to="/app/learn"
          className="type-label-sm text-ink-muted hover:text-ink underline underline-offset-4"
        >
          ← Back to Learn
        </Link>
        <div className="mt-20 type-body-md text-ink-secondary">
          Couldn't load this guide.{" "}
          {(error as { message?: string } | null)?.message}
        </div>
      </div>
    );
  }

  return (
    <StepModule
      key={guide.slug}
      guide={guide}
      open={open}
      onOpenChange={(next) => {
        if (!next && open && !closing.current) {
          closing.current = true;
          track("guide_completed", { slug: guide.slug });
          // Let the drawer exit over the still-mounted Learn library.
          // Cleanup prevents a late redirect if another navigation wins.
          closeTimer.current = setTimeout(
            () => navigate("/app/learn"),
            DRAWER_MS,
          );
        }
        setOpen(next);
      }}
    />
  );
}
